import { useState, useEffect, useRef, useCallback } from 'react';
import { config } from '../config/appConfig';
import { getLyricsForTrack, getLrcUrlForTrack, cacheParsedLyrics } from '../config/karaokeConfig';
import { parseLRC } from '../utils/lrcParser';

const trackEvent = (url, data = {}) => {
    // Используем keepalive для надежной отправки при размонтировании/закрытии страницы
    fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true
    }).catch(e => console.warn("Analytics error:", e));
};

const importAllMusic = (req) => {
    return req.keys().map(req);
};

let MUSIC_FILES = [];
try {
    const musicContext = require.context('../assets/music', false, /\.(mp3|wav|m4a|aac)$/);
    MUSIC_FILES = importAllMusic(musicContext);
} catch (e) {
    console.warn("Failed to load music context:", e);
}

// Храним индекс последнего трека вне хука, чтобы он сохранялся при переходах между страницами
let lastTrackIndex = -1;

const getNextRandomIndex = (total) => {
    if (total <= 1) return 0;
    let nextIndex;
    // Гарантируем, что следующий трек не будет таким же, как предыдущий
    do {
        nextIndex = Math.floor(Math.random() * total);
    } while (nextIndex === lastTrackIndex);
    lastTrackIndex = nextIndex;
    return nextIndex;
};

export const useBackgroundMusic = (isPlaying) => {
    const [volume, setVolume] = useState(0.3);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [lyrics, setLyrics] = useState([]);
    const [analyser, setAnalyser] = useState(null);
    const activeAudioRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const isEnabled = config.features?.musicEasterEgg ?? true;

    const startTimeRef = useRef(null);
    const stayTimeoutRef = useRef(null);
    const analyticsTrackedRef = useRef({ access: false, result: false });

    // Состояние для принудительного перезапуска трека (например, при завершении текущего)
    const [trackVersion, setTrackVersion] = useState(0);
    const wasPlayingRef = useRef(false);

    useEffect(() => {
        let timeoutId;

        if (isEnabled && isPlaying && MUSIC_FILES.length > 0) {
            let currentMuted = isMuted;
            let currentVolume = volume;

            // Если мы только что включили модуль (новая сессия, а не смена трека)
            if (!wasPlayingRef.current) {
                currentMuted = false;
                if (currentVolume === 0) currentVolume = 0.3;
                setIsMuted(false);
                setVolume(currentVolume);
                wasPlayingRef.current = true;
            }

            const targetVolume = currentMuted ? 0 : currentVolume;

            const randomIndex = getNextRandomIndex(MUSIC_FILES.length);
            const randomMusic = MUSIC_FILES[randomIndex];

            // Более надежное извлечение имени трека (убираем путь, расширение и хеши Webpack)
            // Пример: /static/media/track.a1b2c3d4.mp3 -> track
            const fileName = randomMusic.split('/').pop();
            const cleanName = fileName.split('.')[0];
            const decodedName = decodeURIComponent(cleanName).replace(/\.[a-f0-9]{8,20}$/, '');

            setCurrentTrack(decodedName);

            // Асинхронная загрузка LRC через fetch
            const lrcUrl = getLrcUrlForTrack(decodedName);
            if (lrcUrl) {
                fetch(lrcUrl)
                    .then(response => response.text())
                    .then(text => {
                        const parsed = parseLRC(text);
                        setLyrics(parsed);
                        cacheParsedLyrics(decodedName, parsed);
                        console.log(`LRC loaded and parsed for: ${decodedName} (${parsed.length} lines)`);
                    })
                    .catch(err => {
                        console.warn("Failed to fetch LRC:", err);
                        setLyrics([]);
                    });
            } else {
                // Пытаемся взять из кэша если файла нет
                setLyrics(getLyricsForTrack(decodedName));
            }

            const audio = new Audio(randomMusic);
            audio.crossOrigin = "anonymous"; // Важно для Web Audio API
            audio.volume = targetVolume;
            activeAudioRef.current = audio;

            // Инициализация Web Audio API для анализа бита
            try {
                if (!audioContextRef.current) {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                const ctx = audioContextRef.current;

                if (!analyserRef.current) {
                    analyserRef.current = ctx.createAnalyser();
                    analyserRef.current.fftSize = 256;
                    setAnalyser(analyserRef.current);
                }

                // Пересоздаем source только если аудио элемент изменился
                sourceRef.current = ctx.createMediaElementSource(audio);
                sourceRef.current.connect(analyserRef.current);
                analyserRef.current.connect(ctx.destination);
            } catch (e) {
                console.warn("AudioContext analysis failed:", e);
            }

            const handleTimeUpdate = () => {
                setCurrentTime(audio.currentTime);
            };
            audio.addEventListener('timeupdate', handleTimeUpdate);

            // Автоматическое переключение на следующий трек по завершении
            const handleEnded = () => {
                // При смене трека нужно сбросить аналитику
                analyticsTrackedRef.current = { access: false, result: false };
                startTimeRef.current = null;
                setTrackVersion(v => v + 1);
            };
            audio.addEventListener('ended', handleEnded);

            timeoutId = setTimeout(() => {
                if (isPlaying && activeAudioRef.current === audio) {
                    audio.play().then(() => {
                        if (!analyticsTrackedRef.current.access) {
                            // Отправляем событие доступа. Больше не используем sessionStorage, 
                            // чтобы статистика учитывала каждое использование функции или обновление страницы.
                            trackEvent(config.api.analytics.audioAccess, { trackName: decodedName });
                            analyticsTrackedRef.current.access = true;
                            startTimeRef.current = Date.now();

                            // Запускаем таймер лояльности (30 сек) сразу после успешного старта
                            stayTimeoutRef.current = setTimeout(() => {
                                if (!analyticsTrackedRef.current.result) {
                                    trackEvent(config.api.analytics.musicStay, { trackName: decodedName });
                                    analyticsTrackedRef.current.result = true;
                                }
                            }, 30000);
                        }
                    }).catch(e => {
                        if (e.name === 'NotAllowedError') {
                            console.log("Autoplay blocked by browser policy");
                            trackEvent(config.api.analytics.autoplayBlock, { trackName: decodedName });
                        }
                    });
                }
            }, config.features?.musicDelay ?? 2000);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (stayTimeoutRef.current) clearTimeout(stayTimeoutRef.current);
            if (activeAudioRef.current) {
                // Вызываем очистку звука только при отмонтировании или смене компонента/трека.
                // При этом мы НЕ сбрасываем analyticsTrackedRef.current, чтобы не дублировать статы
                activeAudioRef.current.pause();
                activeAudioRef.current.currentTime = 0;
                activeAudioRef.current = null;
            }
        };
    }, [isPlaying, isEnabled, trackVersion]);

    useEffect(() => {
        if (activeAudioRef.current) {
            activeAudioRef.current.volume = isMuted ? 0 : volume;
        }

        // Логика отслеживания заглушения
        if (startTimeRef.current && !analyticsTrackedRef.current.result) {
            if (isMuted || volume === 0) {
                const secondsToMute = (Date.now() - startTimeRef.current) / 1000;
                trackEvent(config.api.analytics.musicMute, {
                    seconds: secondsToMute,
                    trackName: currentTrack
                });
                analyticsTrackedRef.current.result = true;
            }
        }
    }, [volume, isMuted, currentTrack]);

    // Логика отслеживания при уходе со страницы или остановке функции
    useEffect(() => {
        if (!isPlaying) {
            wasPlayingRef.current = false;
            if (activeAudioRef.current) {
                activeAudioRef.current.pause();
                activeAudioRef.current.currentTime = 0;
                activeAudioRef.current = null;
            }
            if (stayTimeoutRef.current) {
                clearTimeout(stayTimeoutRef.current);
            }
            // Если функцию выключили вручную, проверяем, прослушал ли он достаточно долго
            if (startTimeRef.current && !analyticsTrackedRef.current.result) {
                const duration = (Date.now() - startTimeRef.current) / 1000;
                if (duration > 30) {
                    trackEvent(config.api.analytics.musicStay, { trackName: currentTrack });
                }
                analyticsTrackedRef.current.result = true;
            }
            // Сбрасываем рефы при остановке, чтобы при следующем запуске на этой же странице статистика сработала снова
            analyticsTrackedRef.current = { access: false, result: false };
            startTimeRef.current = null;
        }

        return () => {
            if (startTimeRef.current && !analyticsTrackedRef.current.result) {
                const duration = (Date.now() - startTimeRef.current) / 1000;
                if (duration > 30) {
                    trackEvent(config.api.analytics.musicStay, { trackName: currentTrack });
                }
                analyticsTrackedRef.current.result = true;
            }
        };
    }, [isPlaying]);

    const nextTrack = useCallback(() => {
        analyticsTrackedRef.current = { access: false, result: false };
        startTimeRef.current = null;
        if (activeAudioRef.current) {
            activeAudioRef.current.pause();
            activeAudioRef.current.currentTime = 0;
            activeAudioRef.current = null;
        }
        setTrackVersion(v => v + 1);
    }, []);

    return {
        volume,
        setVolume,
        isMuted,
        setIsMuted,
        currentTime,
        currentTrack,
        lyrics,
        isEnabled,
        analyser,
        nextTrack
    };
};
